/**
 * ResizableSxSPanes Component
 *
 * Task 6: Resizable two-pane layout for Side-by-Side comparison.
 * - Drag handle between panes
 * - Keyboard support (Left/Right arrows adjust ±5%)
 * - Bounds: 30% to 70% for left pane
 * - Session-only (no localStorage persistence)
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { logger } from '../../../lib/logging';

export interface ResizableSxSPanesProps {
  /** Left pane content */
  leftPane: React.ReactNode;
  /** Right pane content */
  rightPane: React.ReactNode;
  /** Initial split percentage (0-100) for left pane, default 50 */
  initialSplitPct?: number;
  /** Callback when split changes */
  onSplitChange?: (splitPct: number) => void;
  /** Correlation ID for telemetry */
  correlationId?: string;
}

export const ResizableSxSPanes: React.FC<ResizableSxSPanesProps> = ({
  leftPane,
  rightPane,
  initialSplitPct = 50,
  onSplitChange,
  correlationId
}) => {
  const MIN_SPLIT = 30;
  const MAX_SPLIT = 70;
  const KEYBOARD_STEP = 5;

  const [splitPct, setSplitPct] = useState(() => {
    // Clamp initial value
    return Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, initialSplitPct));
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);

  // Handle mouse drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    logger.info('sxs_resize_start', correlationId, {
      op: 'sxs_resize_start',
      component: 'ResizableSxSPanes',
      correlationId,
      currentSplitPct: splitPct,
      success: true
    });
  }, [correlationId, splitPct]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const newSplitPct = ((e.clientX - rect.left) / rect.width) * 100;
    const clampedSplitPct = Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, newSplitPct));

    setSplitPct(clampedSplitPct);
    onSplitChange?.(clampedSplitPct);
  }, [isDragging, onSplitChange]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    logger.info('sxs_resize_end', correlationId, {
      op: 'sxs_resize_end',
      component: 'ResizableSxSPanes',
      correlationId,
      finalSplitPct: splitPct,
      success: true
    });
  }, [isDragging, correlationId, splitPct]);

  // Attach global mouse listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Keyboard support (Left/Right arrows)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const newSplitPct = Math.max(MIN_SPLIT, splitPct - KEYBOARD_STEP);
      setSplitPct(newSplitPct);
      onSplitChange?.(newSplitPct);

      logger.info('sxs_resize_keyboard', correlationId, {
        op: 'sxs_resize_keyboard',
        component: 'ResizableSxSPanes',
        correlationId,
        direction: 'left',
        newSplitPct,
        success: true
      });
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const newSplitPct = Math.min(MAX_SPLIT, splitPct + KEYBOARD_STEP);
      setSplitPct(newSplitPct);
      onSplitChange?.(newSplitPct);

      logger.info('sxs_resize_keyboard', correlationId, {
        op: 'sxs_resize_keyboard',
        component: 'ResizableSxSPanes',
        correlationId,
        direction: 'right',
        newSplitPct,
        success: true
      });
    }
  }, [splitPct, onSplitChange, correlationId]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full relative"
      data-testid="resizable-sxs-panes"
    >
      {/* Left pane */}
      <div
        className="overflow-auto"
        style={{ width: `${splitPct}%` }}
        data-testid="sxs-left-pane"
      >
        {leftPane}
      </div>

      {/* Resizable divider */}
      <div
        ref={dividerRef}
        role="separator"
        aria-label="Resize panes (Left/Right arrow keys to adjust)"
        aria-valuenow={Math.round(splitPct)}
        aria-valuemin={MIN_SPLIT}
        aria-valuemax={MAX_SPLIT}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        className={`
          flex-shrink-0 w-3 bg-slate-600/70 cursor-col-resize
          hover:bg-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-900
          transition-colors transition-shadow duration-150
          shadow-[0_0_0_1px_rgba(15,23,42,0.6),0_0_0_2px_rgba(34,211,238,0.2)]
          ${isDragging ? 'bg-cyan-400/70 shadow-[0_0_0_1px_rgba(15,23,42,0.7),0_0_0_3px_rgba(34,211,238,0.45)]' : ''}
        `}
        data-testid="sxs-divider"
      >
        {/* Visual grip indicator */}
        <div className="h-full w-full flex items-center justify-center">
          <div className="flex flex-col gap-1 items-center justify-center">
            <span className="block h-8 w-0.5 rounded-full bg-white/80" />
            <span className="block h-1 w-5 rounded-full bg-white/30" />
          </div>
        </div>
      </div>

      {/* Right pane */}
      <div
        className="flex-1 overflow-auto"
        data-testid="sxs-right-pane"
      >
        {rightPane}
      </div>
    </div>
  );
};
